import { getConfig, getErrorMessage, klinikenApi } from "@/api";
import dialogEvents from "@/dialogEvents";
import { flashMessage, openDialog } from "@/utils";

const getDefaultState = () => {
    return {
        currentId: null,
        patientData: {},
        patientDataHidden: false,
        editPatientId: null,
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        reset(state) {
            Object.assign(state, getDefaultState());
        },
        load(state, patient) {
            state.currentId = patient.id;
            state.patientData = patient;
            if (process.env.NODE_ENV === "development") {
                sessionStorage.setItem("PatientId", state.currentId);
            }
        },
        hidePatientData(state, hidden) {
            state.patientDataHidden = hidden;
        },
        setEditPatient(state, id) {
            state.editPatientId = id;
        },
    },
    /**
     *  Lots of failsafes during testing.
     */
    getters: {
        /*
            TODO: check for id type. Reservnummer usually has format YYYYMMDDXXXX, but is all kinds of arbitrary with letters as well as numbers.
            A simple check for ID type "1.2.752.129.2.1.3.1" should suffice, everything else is reservnummer.
            Note that DOB is *not* always the first 6 numbers of personnummer and DOB should always be used.
        */

        /**
         *  Experimental code to return custom table getters for sensitive information.
         */
        personIdGetter(state) {
            if (!state.patientDataHidden)
                return (personId) => {
                    return personId;
                };
            else
                return (personId) => {
                    return personId ? personId.slice(0, 4) + "XXXX-XXXX" : null;
                };
        },
        personIdFormatedGetter(state) {
            if (!state.patientDataHidden)
                return (personId_formatted) => {
                    return personId_formatted;
                };
            else
                return (personId) => {
                    return personId ? personId.slice(0, 4) + "-XXXX-XXXX" : null;
                };
        },
        nameGetter(state) {
            if (!state.patientDataHidden)
                return (fornamn, efternamn) => {
                    return fornamn && efternamn ? fornamn + " " + efternamn : null;
                };
            // new String(fornamn / efternamn) necessary since fornamn can be null.
            else
                return (fornamn, efternamn) => {
                    return fornamn && efternamn ? fornamn.charAt(0) + "*** " + efternamn.charAt(0) + "***" : null;
                };
        },
        personId(state, getters) {
            return getters.personIdGetter(state.patientData.personId);
        },
        personId_formatted(state, getters) {
            return getters.personIdFormatedGetter(state.patientData.personId_formatted);
        },
        careCoverage(state) {
            return state.patientData.careCoverage;
        },
        fornamn(state) {
            return state.patientData.fornamn;
        },
        efternamn(state) {
            return state.patientData.efternamn;
        },
        tilltalsnamnIndex(state) {
            return state.patientData.tilltalsnamn_index;
        },
        namn(state) {
            return state.patientData.namn;
        },
        name(getters) {
            // deprecated
            return getters.namn;
        },
        tilltalsnamn(state) {
            return state.patientData.tilltalsnamn;
        },
        fornamnslista(state) {
            return state.patientData.fornamnslista;
        },
        fullstandigtNamn(state) {
            return state.patientData.fullstandigtNamn;
        },

        gender(state, getters) {
            if (state.patientData.gender != null)
                if (state.patientData.gender.displayName != null) return state.patientData.gender.displayName;
            return getters.binaryGender;
        },
        binaryGender(state) {
            if (state.currentId) return parseInt(state.patientData.personId.charAt(10)) % 2 === 0 ? "Kvinna" : "Man";
            else return "";
        },
        address(state) {
            return (
                (!state.coAdress
                    ? (state.patientData.adressRad1 + " " || "") + (state.patientData.adressRad2 + " " || "")
                    : state.coAdress + " ") +
                state.patientData.postnummer +
                " " +
                state.patientData.postort
            );
        },
        dateOfBirth(state) {
            if (state.currentId) {
                return state.patientData.dateOfBirth || state.patientData.personId.slice(0, 8);
            }
            return "";
        },
        editPatient(state) {
            return state.editPatientId;
        },
        currentId(state) {
            return state.currentId;
        },
        patient(state) {
            return state.patientData;
        },
        avliden(state) {
            return state.patientData.avliden;
        },
        sekretessmarkering(state) {
            return state.patientData.sekretess;
        },
        skyddadIdentitet(state) {
            return state.patientData.skyddad_identitet;
        },
        extraData(state) {
            return state.patientData.extra_data;
        },
    },
    actions: {
        async _loadFromBackend({ commit, dispatch }, { id = "current", failSilently = false }) {
            dispatch("tabs/closeAllPatientBound", null, { root: true });
            // START Patientkopplad data som ligger lagrad i store och ska rensas vid byte av patient
            commit("receptforskrivning/reset", null, { root: true });
            commit("nll/reset", null, { root: true });
            commit("alertInformation/reset", null, { root: true });
            // END Patientkopplad data som ligger lagrad i store och ska rensas vid byte av patient

            return klinikenApi
                .get("core/patienter/" + id + "/", getConfig())
                .then((response) => {
                    commit("load", response.data);
                    dispatch(
                        "alertInformation/loadAlertInformationSymbol",
                        { patientId: response.data.id },
                        { root: true }
                    );
                    flashMessage("Aktiv patient: " + response.data.personId_formatted);
                })
                .catch((e) => {
                    if (!failSilently) {
                        openDialog("Kunde inte hämta patient från servern." + getErrorMessage(e), "warning");
                    }
                });
        },
        async loadFromBackend({ dispatch, state, rootGetters }, { id = "current", failSilently = false }) {
            return new Promise((resolve, reject) => {
                if (state.currentId !== id) {
                    let hasUnsavedData = false;

                    let tabId = undefined;
                    rootGetters["tabs/tabs"].forEach((tab) => {
                        if (tab.patientBound) {
                            hasUnsavedData = tab.hasUnsavedData ? true : hasUnsavedData;
                            tabId = tab.id;
                        }
                    });

                    if (hasUnsavedData) {
                        dialogEvents.$emit("open", {
                            type: "warning",
                            description:
                                "Du har osparad data för tidigare vald patient. Vill du gå vidare utan att spara?",
                            buttons: [
                                {
                                    title: "Avbryt",
                                    type: "secondary",
                                    action: () => {
                                        dispatch("tabs/showTab", { id: tabId }, { root: true });
                                        reject();
                                    },
                                },
                                {
                                    title: "Ja, gå vidare utan att spara",
                                    type: "primary",
                                    action: async () => {
                                        dispatch("_loadFromBackend", { id, failSilently })
                                            .then(() => {
                                                resolve();
                                            })
                                            .catch(() => {
                                                reject();
                                            });
                                    },
                                },
                            ],
                        });
                    } else {
                        dispatch("_loadFromBackend", { id, failSilently })
                            .then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                    }
                } else {
                    resolve();
                }
            });
        },
        resetPatientData({ commit, dispatch, state, rootGetters }) {
            const firstUnsavedTab = rootGetters["tabs/tabs"].find((tab) => tab.patientBound && tab.hasUnsavedData);

            if (firstUnsavedTab) {
                dialogEvents.$emit("open", {
                    type: "warning",
                    description: "Du har osparad data för den valda patienten. Vill du gå vidare utan att spara?",
                    buttons: [
                        {
                            title: "Avbryt",
                            type: "secondary",
                            action: () => {
                                dispatch("tabs/showTab", { id: firstUnsavedTab.id }, { root: true });
                            },
                        },
                        {
                            title: "Ja, gå vidare utan att spara",
                            type: "primary",
                            action: () => {
                                dispatch("tabs/closeAllPatientBound", null, { root: true });
                                commit("reset");
                            },
                        },
                    ],
                });
            } else {
                dispatch("tabs/closeAllPatientBound", null, { root: true });
                commit("reset");
            }
        },
    },
};
